import React from 'react';

import type { DefaultContext, OperationVariables } from '@apollo/client';
import _ from 'es-toolkit/compat';

import { useAuthCredentials } from '@hrlogics/react-auth';

import type { Nullable } from '@clearverify/types';

import { useCurrentUserLazyQuery } from 'src/api';
import { useCognito } from 'src/hooks/app';

import { AuthContext, AuthContextType } from './AuthContext';
import { UserNotProvisionedError } from './errors';
import { isForbiddenError } from './utils';

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { accessToken, isAuthorized, setAuthCredentials, resetAuthCredentials } =
    useAuthCredentials();

  const { signIn, signOut } = useCognito();

  const [currentUserQuery] = useCurrentUserLazyQuery(commonQueryOptions);

  const logout: AuthContextType['logout'] = React.useCallback(async () => {
    // Local credentials must be dropped even if Cognito rejects the sign-out.
    if (accessToken) await signOut({ accessToken }).catch(() => undefined);
    resetAuthCredentials();
  }, [accessToken, resetAuthCredentials, signOut]);

  const setAuthState: AuthContextType['setAuthState'] = React.useCallback(
    async ({ bearerToken, accessToken }) => {
      const context = createContext(bearerToken);

      const { data, error } = await currentUserQuery({ context });

      // Valid Cognito token but no active user in our DB: never persist it, otherwise the app
      // renders authorized routes with no role (and the SSO callback loops on the URL hash).
      const rejected = error ? isForbiddenError(error) : !data?.currentUser;
      if (rejected) {
        // Drop the Cognito session so the next attempt is not silently re-admitted.
        if (accessToken) await signOut({ accessToken }).catch(() => undefined);
        throw new UserNotProvisionedError();
      }
      // Any other currentUser failure is transient: the Cognito tokens are valid, so persist them and
      // let the app's own currentUser query surface the error (pre-existing behavior).
      setAuthCredentials(bearerToken, accessToken);
    },
    [currentUserQuery, setAuthCredentials, signOut],
  );

  const login: AuthContextType['login'] = React.useCallback(
    async ({ email, password, session, userCode }) => {
      let bearerToken: Nullable<string>;
      let accessToken: Nullable<string>;

      if (session && userCode) {
        const signInData = await signIn({
          username: email,
          session,
          userCode,
        });

        bearerToken = signInData.idToken;
        accessToken = signInData.accessToken;
      } else if (password) {
        const signInData = await signIn({
          username: email,
          password,
        });

        if (signInData.session) {
          return { success: true, session: signInData.session };
        }

        bearerToken = signInData.idToken;
        accessToken = signInData.accessToken;
      }

      if (_.isNil(bearerToken)) {
        throw new Error('Authorization Error');
      }
      await setAuthState({ bearerToken, accessToken: accessToken ?? undefined });

      return { success: true };
    },
    [setAuthState, signIn],
  );

  const contextValue = React.useMemo((): AuthContextType => {
    return {
      isAuthorized,
      login,
      logout,
      setAuthState,
    };
  }, [isAuthorized, login, logout, setAuthState]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const commonQueryOptions: OperationVariables = {
  fetchPolicy: 'network-only',
};

const createContext = (token: string): DefaultContext => {
  return { headers: { authorization: `Bearer ${token}` } };
};
